"use client";

import * as React from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import type { SidebarIconRailItem } from "../sidebar-icon-rail";
import { Z_INDEX } from "../z-index";
import { SidebarLiquidButton } from "./sidebar-liquid-button";

export interface SidebarLiquidRailProps {
  items: SidebarIconRailItem[];
  isCurrentPath: (path: string) => boolean;
  onNavigate: (path: string) => void;
  className?: string;
  revealItems?: boolean;
  expandTo?: "left" | "right";
}

function isRailItemActive(
  item: SidebarIconRailItem,
  isCurrentPath: (path: string) => boolean
): boolean {
  const paths = item.matchPaths?.length
    ? item.matchPaths
    : item.path
      ? [item.path]
      : [];

  return paths.length > 0 && paths.some((path) => isCurrentPath(path));
}

function SidebarLiquidGroup({
  item,
  isCurrentPath,
  onNavigate,
  expandTo,
  revealItems,
  revealIndex,
  ensureVisible
}: {
  item: SidebarIconRailItem & { children: SidebarIconRailItem[] };
  isCurrentPath: (path: string) => boolean;
  onNavigate: (path: string) => void;
  expandTo: "left" | "right";
  revealItems: boolean;
  revealIndex: number;
  ensureVisible?: (element: HTMLElement, options?: { extraBottom?: number; immediate?: boolean }) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const closeTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const groupRef = React.useRef<HTMLDivElement>(null);
  const isActive = isRailItemActive(item, isCurrentPath);

  React.useEffect(
    () => () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
    },
    []
  );

  const closeGroup = React.useCallback(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(false);
  }, []);

  const toggleGroup = React.useCallback(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen((current) => !current);
  }, []);

  const handleBlur = React.useCallback(
    (event: React.FocusEvent<HTMLDivElement>) => {
      if (event.currentTarget.contains(event.relatedTarget)) return;
      closeGroup();
    },
    [closeGroup]
  );

  const handleChildNavigation = React.useCallback(
    (path: string) => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
      setOpen(false);
      onNavigate(path);
    },
    [onNavigate]
  );

  React.useEffect(() => {
    if (!open) return;

    if (groupRef.current && ensureVisible) {
      ensureVisible(groupRef.current, {
        extraBottom: item.children.length * 34 + 20,
      });
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeGroup();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, closeGroup, ensureVisible, item.children.length]);

  return (
    <div
      ref={groupRef}
      className={cn(
        "sidebar-liquid-rail-item relative flex w-full flex-col overflow-visible",
        expandTo === "right" ? "items-end" : "items-start",
        revealItems && "sidebar-liquid-rail-reveal motion-safe:opacity-0"
      )}
      style={{
        ...(revealItems ? { animationDelay: `${80 + revealIndex * 45}ms` } : undefined),
        zIndex: open ? Z_INDEX.DROPDOWN : undefined
      }}
      data-group-open={open ? "true" : undefined}
      onBlur={handleBlur}
    >
      <SidebarLiquidButton
        icon={open ? <ChevronDown className="h-4 w-4" /> : item.icon}
        label={item.name}
        variant={item.variant ?? item.tone ?? "ocean"}
        active={isActive || open}
        expanded={open}
        expandTo={expandTo}
        className={undefined}
        onClick={toggleGroup}
        aria-label={item.name}
        aria-expanded={open}
      />

      <div
        className={cn(
          "sidebar-liquid-inline-submenus relative flex flex-col gap-1 overflow-visible transition-[max-height,opacity,margin] duration-300 ease-out",
          open ? "mt-1 opacity-100" : "mt-0 opacity-0"
        )}
        style={{
          maxHeight: open ? `${item.children.length * 34 + 10}px` : "0px",
          zIndex: open ? Z_INDEX.DROPDOWN : undefined,
          clipPath: open
            ? expandTo === "right"
              ? "inset(0 -320px 0 0)"
              : "inset(0 0 0 -320px)"
            : expandTo === "right"
              ? "inset(0 -320px 100% 0)"
              : "inset(0 0 100% -320px)"
        }}
      >
        {open
          ? item.children.map((child, index) => {
              const childActive = Boolean(child.path && isCurrentPath(child.path));
              const variant = child.variant ?? child.tone ?? item.variant ?? item.tone ?? "ocean";

              return (
                <div
                  key={child.id}
                  className="sidebar-liquid-inline-submenu-item sidebar-liquid-inline-submenu-item--down"
                  style={{ animationDelay: `${index * 42}ms` }}
                >
                  <SidebarLiquidButton
                    icon={child.icon}
                    label={child.name}
                    variant={variant}
                    active={childActive}
                    expanded={open}
                    compact
                    expandTo={expandTo}
                    onClick={() => child.path && handleChildNavigation(child.path)}
                    aria-label={child.name}
                  />
                </div>
              );
            })
          : null}
      </div>
    </div>
  );
}

export function SidebarLiquidRail({
  items,
  isCurrentPath,
  onNavigate,
  className,
  revealItems = false,
  expandTo = "right"
}: SidebarLiquidRailProps) {
  const navRef = React.useRef<HTMLElement>(null);
  const trackRef = React.useRef<HTMLDivElement>(null);
  const maxDragOffsetRef = React.useRef(0);
  const dragRef = React.useRef({
    active: false,
    pointerId: -1,
    startY: 0,
    startOffset: 0,
    moved: false
  });
  const suppressClickRef = React.useRef(false);
  const [dragOffset, setDragOffset] = React.useState(0);
  const [dragging, setDragging] = React.useState(false);
  const [maxDragOffset, setMaxDragOffset] = React.useState(0);

  const clampOffset = React.useCallback((value: number) => {
    const maxOffset = maxDragOffsetRef.current;
    return Math.min(0, Math.max(-maxOffset, value));
  }, []);

  const measureDragBounds = React.useCallback(() => {
    const nav = navRef.current;
    const track = trackRef.current;
    const parent = nav?.parentElement;
    if (!nav || !track || !parent) return;

    // Sin margen artificial: solo permitir scroll cuando realmente el contenido
    // excede el alto visible del contenedor.
    const maxOffset = Math.max(0, track.scrollHeight - parent.clientHeight);
    maxDragOffsetRef.current = maxOffset;
    setMaxDragOffset((current) => (Math.abs(current - maxOffset) < 0.5 ? current : maxOffset));
    setDragOffset((current) => {
      const clamped = Math.min(0, Math.max(-maxOffset, current));
      return Math.abs(current - clamped) < 0.5 ? current : clamped;
    });
  }, []);

  React.useLayoutEffect(() => {
    measureDragBounds();

    const nav = navRef.current;
    const parent = nav?.parentElement;
    if (!nav || !parent || typeof ResizeObserver === "undefined") return;

    const ro = new ResizeObserver(measureDragBounds);
    ro.observe(nav);
    if (trackRef.current) ro.observe(trackRef.current);
    ro.observe(parent);
    return () => ro.disconnect();
  }, [items.length, measureDragBounds]);

  const ensureVisible = React.useCallback((element: HTMLElement, options?: { extraBottom?: number; immediate?: boolean }) => {
    const nav = navRef.current;
    const parent = nav?.parentElement;
    if (!nav || !parent) return;

    const parentRect = parent.getBoundingClientRect();
    const itemRect = element.getBoundingClientRect();
    const extraBottom = options?.extraBottom ?? 0;

    const overBottom = itemRect.bottom + extraBottom - parentRect.bottom;
    const overTop = parentRect.top - itemRect.top;

    let delta = 0;
    if (overBottom > 0) {
      delta = -(overBottom + 8);
    } else if (overTop > 0) {
      delta = overTop + 8;
    }

    if (delta === 0) return;
    setDragOffset((current) => clampOffset(current + delta));
  }, [clampOffset]);

  const handlePointerDown = React.useCallback((event: React.PointerEvent<HTMLElement>) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    if (maxDragOffsetRef.current <= 0) return;

    dragRef.current = {
      active: true,
      pointerId: event.pointerId,
      startY: event.clientY,
      startOffset: dragOffset,
      moved: false
    };
    setDragging(true);
  }, [dragOffset]);

  const handlePointerMove = React.useCallback((event: React.PointerEvent<HTMLElement>) => {
    const state = dragRef.current;
    if (!state.active || state.pointerId !== event.pointerId) return;

    const deltaY = event.clientY - state.startY;
    const didDrag = Math.abs(deltaY) > 10;
    if (!didDrag) return;

    state.moved = true;
    suppressClickRef.current = true;

    const maxOffset = maxDragOffsetRef.current;
    const next = Math.min(0, Math.max(-maxOffset, state.startOffset + deltaY));
    setDragOffset(next);
    event.preventDefault();
  }, []);

  const stopDragging = React.useCallback((event: React.PointerEvent<HTMLElement>) => {
    const state = dragRef.current;
    if (!state.active || state.pointerId !== event.pointerId) return;

    dragRef.current.active = false;
    setDragging(false);

    window.setTimeout(() => {
      suppressClickRef.current = false;
    }, state.moved ? 80 : 0);
  }, []);

  const handleClickCapture = React.useCallback((event: React.MouseEvent<HTMLElement>) => {
    if (!suppressClickRef.current) return;
    event.preventDefault();
    event.stopPropagation();
  }, []);

  React.useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;

    const handleNativeWheel = (event: WheelEvent) => {
      if (event.deltaY === 0) return;

      // Listener nativo non-passive: asegura que el wheel sobre el sidebar NO
      // llegue al contenedor principal scrollable (`#app-main-scroll`).
      event.preventDefault();
      event.stopPropagation();

      const maxOffset = maxDragOffsetRef.current;
      if (maxOffset <= 0) return;

      setDragOffset((current) => clampOffset(current - event.deltaY));
    };

    nav.addEventListener("wheel", handleNativeWheel, { passive: false });
    return () => nav.removeEventListener("wheel", handleNativeWheel);
  }, [clampOffset]);

  if (items.length === 0) return null;

  return (
    <nav
      ref={navRef}
      aria-label="Navegación principal"
      className={cn(
        "sidebar-liquid-rail relative overflow-visible py-1.5",
        "select-none touch-none cursor-grab active:cursor-grabbing",
        dragging && "sidebar-liquid-rail--dragging",
        className
      )}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={stopDragging}
      onPointerCancel={stopDragging}
      onClickCapture={handleClickCapture}
    >

      <div
        ref={trackRef}
        className={cn(
          "sidebar-liquid-rail-track relative flex flex-col gap-1 overflow-visible pb-2",
          expandTo === "right" ? "items-end pr-0.5" : "items-start pl-0.5"
        )}
        style={{ transform: `translate3d(0, ${dragOffset}px, 0)` }}
      >
        {items.map((item, index) => {
        if (item.children?.length) {
          return (
            <SidebarLiquidGroup
              key={item.id}
              item={item as SidebarIconRailItem & { children: SidebarIconRailItem[] }}
              isCurrentPath={isCurrentPath}
              onNavigate={onNavigate}
              expandTo={expandTo}
              revealItems={revealItems}
              revealIndex={index}
              ensureVisible={ensureVisible}
            />
          );
        }

        return (
          <div
            key={item.id}
            className={cn(
              "sidebar-liquid-rail-item relative flex w-full overflow-visible",
              expandTo === "right" ? "justify-end" : "justify-start",
              revealItems && "sidebar-liquid-rail-reveal motion-safe:opacity-0"
            )}
            style={
              revealItems
                ? { animationDelay: `${80 + index * 45}ms` }
                : undefined
            }
          >
            <SidebarLiquidButton
              icon={item.icon}
              label={item.name}
              variant={item.variant ?? item.tone ?? "ocean"}
              active={isRailItemActive(item, isCurrentPath)}
              expandTo={expandTo}
              onClick={() => item.path && onNavigate(item.path)}
              aria-label={item.name}
            />
          </div>
        );
        })}
      </div>

      <style jsx global>{`
        @keyframes sidebar-liquid-rail-reveal {
          from { opacity: 0; transform: translateY(-6px); filter: blur(3px); }
          to { opacity: 1; transform: none; filter: none; }
        }

        @keyframes sidebar-liquid-inline-submenu-item-reveal-down {
          0% {
            opacity: 0;
            transform: translateY(-10px) scale(0.38);
            filter: blur(8px) saturate(1.25);
            border-radius: 9999px;
          }
          34% {
            opacity: 1;
            transform: translateY(2px) scale(1.08);
            filter: blur(1px) saturate(1.18);
            border-radius: 9999px;
          }
          64% {
            opacity: 1;
            transform: translateY(-1px) scale(0.96);
            filter: blur(0px) saturate(1.08);
            border-radius: 9999px;
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
            filter: none;
            border-radius: 9999px;
          }
        }

        @keyframes sidebar-liquid-inline-submenu-item-reveal-up {
          0% {
            opacity: 0;
            transform: translateY(10px) scale(0.38);
            filter: blur(8px) saturate(1.25);
            border-radius: 9999px;
          }
          34% {
            opacity: 1;
            transform: translateY(-2px) scale(1.08);
            filter: blur(1px) saturate(1.18);
            border-radius: 9999px;
          }
          64% {
            opacity: 1;
            transform: translateY(1px) scale(0.96);
            filter: blur(0px) saturate(1.08);
            border-radius: 9999px;
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
            filter: none;
            border-radius: 9999px;
          }
        }

        .sidebar-liquid-rail-reveal {
          animation: sidebar-liquid-rail-reveal 520ms cubic-bezier(0.16,1,0.3,1) forwards;
        }

        .sidebar-liquid-inline-submenus {
          will-change: max-height, opacity;
        }

        .sidebar-liquid-inline-submenu-item {
          transform-origin: center;
        }

        .sidebar-liquid-inline-submenu-item--down {
          animation: sidebar-liquid-inline-submenu-item-reveal-down 460ms cubic-bezier(0.34,1.12,0.64,1) both;
        }

        .sidebar-liquid-inline-submenu-item--up {
          animation: sidebar-liquid-inline-submenu-item-reveal-up 460ms cubic-bezier(0.34,1.12,0.64,1) both;
        }

        #sidebar-v2 .sidebar-liquid-rail,
        #sidebar-v2 .sidebar-liquid-rail-item,
        #sidebar-v2 .sidebar-liquid-button-slot {
          overflow: visible;
        }

        #sidebar-v2 .sidebar-liquid-rail-item {
          min-height: 2.25rem;
        }

        /* El material visual (background, shadow, tint, dark mode, cursor glow,
           deformación) pertenece a LiquidSurface. El rail sólo define layout.
           Los iconos SVG heredan currentColor desde el surface. */
        #sidebar-v2 .sidebar-liquid-button__icon svg {
          color: currentColor;
          stroke: currentColor;
        }

        @media (prefers-reduced-motion: reduce) {
          .sidebar-liquid-rail-reveal,
          .sidebar-liquid-inline-submenu-item {
            animation: none !important;
            opacity: 1 !important;
            transform: none !important;
            filter: none !important;
          }

          #sidebar-v2 .sidebar-liquid-button,
          #sidebar-v2 .sidebar-liquid-button__label {
            transition-duration: 0ms !important;
          }
        }
      `}</style>
    </nav>
  );
}
